import { startTransition, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';

import type { SanitySolutionsTabs } from '#/lib/sanity/queries/sections';
import { useMediaQuery } from '#/lib/useMatchMedia';
import { cn, COLORS } from '#/lib/utils';
import { Button } from '#/components/Button';
import { FormattedText } from '#/components/FormattedText';
import { Headline } from '#/components/Headline';
import { Media } from '#/components/Media';

import { Overline } from './Overline';

export function SolutionsTabs(props: SanitySolutionsTabs) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedScrollSnap, setSelectedScrollSnap] = useState(0);
  // Once a tab has been shown, keep its Media mounted so revisiting is cheap
  // without paying for every tab's media on first paint / every click.
  const [mountedTabs, setMountedTabs] = useState(() => new Set([0]));
  const [matches, hasRun] = useMediaQuery({ screen: 'm' });
  const shouldShowDesktopMedia = matches && hasRun;

  useEffect(() => {
    if (emblaApi) {
      const handleSelect = () => {
        const index = emblaApi.selectedScrollSnap();
        // Embla can fire select during the tab click turn — keep updates off
        // the urgent path so the tab chrome paints first.
        startTransition(() => {
          setSelectedScrollSnap(index);
          setMountedTabs((prev) => {
            if (prev.has(index)) return prev;
            const next = new Set(prev);
            next.add(index);
            return next;
          });
        });
      };

      emblaApi.on('select', handleSelect);

      return () => {
        emblaApi.off('select', handleSelect);
      };
    }
  }, [emblaApi]);

  return (
    <div className="p-main overflow-hidden">
      <div
        className={cn('mx-auto w-full max-w-1235', {
          'max-w-1400': props.renderGrayscale,
        })}
      >
        {props.headline ? (
          <Headline
            className="mx-auto mb-40 w-full max-w-735 text-center"
            value={props.headline}
            variant={3}
          />
        ) : null}
        <div className="no-scrollbar -mx-15 mb-25 space-x-10 overflow-x-auto whitespace-nowrap px-15 text-center">
          {props.tabs.map((tab, tabIndex) => (
            <button
              key={tab._key}
              className={cn(
                'rounded-10 px-16 py-13 text-14 font-[440] leading-100 transition',
                {
                  'bg-[var(--tab-theme-color)]':
                    tabIndex === selectedScrollSnap,
                  'bg-grayscale-01': !props.renderGrayscale,
                  'hover:bg-cipher-01': props.renderGrayscale,
                },
              )}
              style={
                {
                  '--tab-theme-color': props.renderGrayscale
                    ? COLORS.cipher['01']
                    : COLORS[tab.colorTheme ?? 'flame']['01'],
                } as React.CSSProperties
              }
              onClick={() => emblaApi?.scrollTo(tabIndex)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div ref={emblaRef}>
          <div className="flex gap-x-15">
            {props.tabs.map((tab, tabIndex) => {
              const isSelected = tabIndex === selectedScrollSnap;
              const shouldMountMedia = mountedTabs.has(tabIndex);
              return (
                <div
                  key={tab._key}
                  className={cn('min-w-full transition-opacity', {
                    'opacity-0': !isSelected,
                  })}
                >
                  <div
                    className={cn(
                      'flex flex-col rounded-15 m:flex-row m:rounded-25',
                      {
                        '[box-shadow:inset_0_0_0_1px_theme(colors.charcoal)]':
                          !props.renderGrayscale,
                      },
                    )}
                    style={{
                      background: props.renderGrayscale
                        ? COLORS.grayscale['01']
                        : COLORS[tab.colorTheme ?? 'flame']['02'],
                    }}
                  >
                    <div className="p-30 pb-45 m:w-[calc(5/12*100%)] l:p-64 l:pb-90">
                      <div
                        className={cn('w-full max-w-415', {
                          'flex h-full flex-col justify-center':
                            props.renderGrayscale,
                        })}
                      >
                        {tab.eyebrow ? (
                          <Overline className="mb-10" as="span">
                            {tab.eyebrow}
                          </Overline>
                        ) : null}
                        <Headline
                          value={tab.headline}
                          variant={4}
                          className="text-28 m:text-35 l:text-40"
                        />
                        <FormattedText
                          value={tab.content}
                          className="mt-10 text-pretty l:mt-25 m:text-18"
                        />
                        {tab.link?.href ? (
                          <Button
                            href={tab.link.href}
                            variant="link"
                            className="mt-25 m:text-16 l:text-18"
                          >
                            {tab.link.title}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                    <div
                      className={cn(
                        'overflow-clip rounded-15  m:w-[calc(7/12*100%)] m:rounded-25',
                        {
                          'p-45': tab.hasMediaPadding,
                          'border-1 border-charcoal': !props.renderGrayscale,
                        },
                      )}
                      style={{
                        background: props.renderGrayscale
                          ? COLORS.grayscale['01']
                          : COLORS[tab.colorTheme ?? 'flame']['02'],
                      }}
                    >
                      {shouldMountMedia ? (
                        <Media
                          className="size-full max-w-[795px] ml-auto"
                          media={
                            shouldShowDesktopMedia || !tab.mobileMedia
                              ? tab.media
                              : tab.mobileMedia
                          }
                          placeholder={!tab.hasMediaPadding}
                          sizes={[
                            '100vw',
                            {
                              m: '50vw',
                              '1285px': '1000px',
                            },
                          ]}
                        />
                      ) : (
                        <div
                          className="size-full max-w-[795px] ml-auto"
                          aria-hidden
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
